import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import type { CountrySearchResult, LocationSearchResult } from '@sal/location/application/ports';

import { SplitThenSortSearchResultsPipe } from '../pipes/search/split-then-sort-search-results.pipe';
import { PopupDialogService } from './popup-dialog.service';
import { ISearchParams, ISearchParamsAdvanced } from '../interfaces/ISearchParams';
import { environment } from '../../../environments/environment';

@Injectable()
export class SearchService {
    private splitThenSortSearchResultsPipe = inject(SplitThenSortSearchResultsPipe);
    private popupDialogService = inject(PopupDialogService);
    private http = inject(HttpClient);

    private searchResultSubject = new BehaviorSubject<LocationSearchResult[]>([]); // {1}
    private searchValueSubject = new BehaviorSubject<string>(''); // {1}
    private queryValueSubject = new BehaviorSubject<ISearchParamsAdvanced | string>(''); // {1}

    searchResultsUpdates$ = new Observable();
    searchValueUpdates$ = new Observable();
    queryValueUpdates$ = new Observable();

    maxItemsSuggestions = 50;

    searchParamsSource = new BehaviorSubject<ISearchParams | null>({ isAdvancedSearch: false });
    currentSearchParams = this.searchParamsSource.asObservable();

    searchBoxStateSource = new BehaviorSubject<boolean>(false);
    currentSearchBoxState = this.searchBoxStateSource.asObservable();

    constructor() {
        this.searchResultsUpdates$ = this.searchResultSubject.asObservable();
        this.searchValueUpdates$ = this.searchValueSubject.asObservable();
        this.queryValueUpdates$ = this.queryValueSubject.asObservable();
    }

    storeSearchBoxState(newState: boolean) {
        this.searchBoxStateSource.next(newState);
    }

    storeSearchParams(searchParams: ISearchParams | null): void {
        this.searchParamsSource.next(searchParams);
    }

    getSuggestions = (val: string): Promise<LocationSearchResult[]> => {
        if (!val) {
            return Promise.resolve([]);
        }

        return firstValueFrom(
            this.http.get<LocationSearchResult[]>(`${environment.endpoint}/locations/search`, {
                params: new HttpParams().set('searchTerm', val)
            })
        );
    };

    getQueryResults = (searchParams: ISearchParamsAdvanced): Promise<LocationSearchResult[]> => {
        return firstValueFrom(
            this.http.get<LocationSearchResult[]>(`${environment.endpoint}/locations/advanced-search`, {
                params: this.toAdvancedSearchParams(searchParams)
            })
        );
    };

    getQueryData(searchParams: ISearchParamsAdvanced, type: string): Promise<LocationSearchResult[]> {
        this.queryValueSubject.next(searchParams);
        this.searchValueSubject.next('');
        return this.getQueryResults(searchParams).then(searchResponse => {
            this.searchResultSubject.next(searchResponse);
            const result = searchResponse;

            if (result.length === 0) {
                const emptyResult: LocationSearchResult[] = [];
                this.checkIndexDocumentCount();
                return emptyResult;
            } else {
                this.splitThenSortSearchResultsPipe.transform(result, type);
                return result;
            }
        });
    }

    searchCountries(searchString: string): Promise<CountrySearchResult[]> {
        if (!searchString) {
            return Promise.resolve([]);
        }

        return firstValueFrom(
            this.http.get<CountrySearchResult[]>(`${environment.endpoint}/countries/search`, {
                params: new HttpParams().set('searchTerm', searchString)
            })
        );
    }

    searchLocations(searchParams: ISearchParams): Promise<LocationSearchResult[]> {
        if (searchParams.isAdvancedSearch && searchParams.advancedSearchParams) {
            return this.getQueryData(searchParams.advancedSearchParams, 'loccode');
        } else {
            return this.getSearchData(searchParams.searchString ?? '', 'loccode');
        }
    }

    getSearchData(searchTerm: string, type: string): Promise<LocationSearchResult[]> {
        this.searchValueSubject.next(searchTerm || '');
        return this.getSuggestions(searchTerm).then(searchResponse => {
            this.searchResultSubject.next(searchResponse);
            const result = searchResponse;

            if (result.length === 0) {
                const emptyResult: LocationSearchResult[] = [];
                this.checkIndexDocumentCount();
                return emptyResult;
            } else {
                this.splitThenSortSearchResultsPipe.transform(result, type);
                return result;
            }
        });
    }

    checkIndexDocumentCount() {
        // check if there are no documents in the index or simply the
        // search query cannot be resolved to a document
        this.hasData().then(hasData => {
            if (!hasData) {
                // there are no data in the index, so it is assumed to be updated
                this.popupDialogService.open();
            }
        });
    }

    public async hasData() {
        return true; // this.searchLocations({isAdvancedSearch: false, searchString: 'Germany'}).then(res => res.length > 0);
    }

    private toAdvancedSearchParams(searchParams: ISearchParamsAdvanced): HttpParams {
        let params = new HttpParams();
        const parameterEntries: Array<[string, string | boolean | null | undefined]> = [
            ['firm', searchParams.firm],
            ['country', searchParams.country],
            ['city', searchParams.city],
            ['loccode', searchParams.loccode],
            ['postalcode', searchParams.postalcode],
            ['address', searchParams.address],
            ['locationshort', searchParams.locationshort],
            ['section', searchParams.section],
            ['are', searchParams.are],
            ['corporatetype', searchParams.corporatetype],
            ['businessactivity', searchParams.businessactivity],
            ['client', searchParams.client],
            ['headquarter', searchParams.headquarter]
        ];

        for (const [key, value] of parameterEntries) {
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, value.toString());
            }
        }

        return params;
    }
}
